import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import AppGridContainer from '@tenant/core/AppGridContainer';
import {
  Box,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Tooltip,
  Typography,
  Tabs,
  Tab,
} from '@mui/material';
import AppGlossary from '@tenant/core/App/Common/AppGlossary';
import {
  APP_CONNECTIVITY_STATE_ENUM,
  APP_TYPE_ENUM,
  APP_TYPE_ENUM_SELECT,
  LAUNCH_TYPE_ENUM,
  PLATFORM_ENUM,
} from '@tenant/utility/constants/enum';
import checkIsModuleMode from '../../../helpers/checkIsModuleMode';
import FeatureStepWebMarketPlace from './Common/FeatureStepWebMarketPlace';
import MobileStep from './Common/Mobile/MobileStep';
import AppModuleSelect from '@tenant/core/App/Select/AppModuleSelect';
import { generateUUID } from '@tenant/utility/helper/Utils';

import ReplayIcon from '@mui/icons-material/Replay';
import PublishToggle from './Common/elements/PublishToggle';
import ACCESS_SCOPE_ENUM from '@tenant/utility/constants/AppEnum/ACCESS_SCOPE_ENUM';
import useAllowedAccess from '@tenant/core/AppPermission/useAllowedAccess';
import { AuthorizePermission } from '@tenant/utility/constants/authorizePermission';

const ConfigurationSettings = ({
  errors = {},
  values = {},
  setFieldValue = () => { },
}) => {
  const canUpdateConfigPublic = useAllowedAccess([AuthorizePermission.APP_CONFIGURATION_UPDATE_PUBLIC]);

  const isHaveMobileStep = useMemo(() => {
    const platform = values.Platform;
    return (
      platform === PLATFORM_ENUM['Mobile'] ||
      platform === PLATFORM_ENUM['Hybrid']
    );
  }, [values.Platform]);

  const isHaveFeatureStep = useMemo(() => {
    const IntegrationMode = values.IntegrationMode;
    return IntegrationMode === APP_TYPE_ENUM.Embedded;
  }, [values.IntegrationMode]);

  const isShowWebIntegrationMode =
    values.Platform === PLATFORM_ENUM['Hybrid'] ||
    values.Platform === PLATFORM_ENUM['Web'];

  const resetMobileFieldsIfNeeded = (integrationMode) => {
    const isModuleIntegrationMode =
      integrationMode === APP_TYPE_ENUM['Modular'];

    const resetMobileFields = () => {
      setFieldValue('AppConnectivityState', null);
      setFieldValue('BundleZipFile', null);
      setFieldValue('BundleZipBlobReference', '');
      setFieldValue('MobileFeatureUrl', '');
      setFieldValue('MobileAppUrl', '');
      setFieldValue('MobileBAUModuleIDs', []);
    };

    if (isModuleIntegrationMode) {
      resetMobileFields();
    }

    // Select Modular will reset AppConnectivityState and FeatureUrl, so need to update default value again
    if (!isModuleIntegrationMode && values.AppConnectivityState == null) {
      setFieldValue('AppConnectivityState', 0);
      setFieldValue('MobileFeatureUrl', 'https://');
      setFieldValue('MobileAppUrl', 'https://');
    }
  };

  //
  const [activeTab, setActiveTab] = React.useState('PageSlots');
  const handleChangeTab = (event, newValue) => {
    setActiveTab(newValue);
  };

  return (
    <AppGridContainer
      sx={{
        '& .required-sign': {
          color: 'error.main',
        },
      }}
    >
      <Grid item xs={12} md={12}>
        <Box display='flex' justifyContent='space-between'>
          <Typography variant='h4'>Configuration Settings</Typography>
          {canUpdateConfigPublic && (<Box display='flex' alignItems='center'>
            <PublishToggle
              checked={values.IsPublic ?? true}
              onChange={(e) => {
                setFieldValue('IsPublic', e.target.checked);
              }}
            />
            <AppGlossary
              title='Make the app accessible to all users on the marketplace.
        Enabling public access allows any user to view and use the app without restrictions.
        If disabled, the app will be private and only available to specified users or groups'
            />
          </Box>)}
        </Box>
      </Grid>
      <Grid item xs={12} md={4}>
        <Box component='p' color='text.primary' fontSize={14} mb={2}>
          Platform
          <Box component='span' ml={1} sx={{ color: 'error.main' }}>
            *
          </Box>
        </Box>
        <FormControl fullWidth error={Boolean(errors.Platform)}>
          <Select
            value={values.Platform ?? PLATFORM_ENUM['Web']}
            onChange={(e) => {
              const platform = e.target.value;
              setFieldValue('Platform', platform);

              if (
                platform !== PLATFORM_ENUM['Hybrid'] &&
                platform !== PLATFORM_ENUM['Web']
              ) {
                setFieldValue('IntegrationMode', null);
              } else {
                setFieldValue('IntegrationMode', APP_TYPE_ENUM_SELECT[0].value);
              }

              if (
                platform === PLATFORM_ENUM['Hybrid'] &&
                platform === PLATFORM_ENUM['Mobile']
              ) {
                setFieldValue(
                  'AppConnectivityState',
                  APP_CONNECTIVITY_STATE_ENUM['Online'],
                );
              }
            }}
          >
            {Object.entries(PLATFORM_ENUM)
              .map(([value, key]) => ({
                key: key,
                value: value,
              }))
              .map((item) => (
                <MenuItem key={item.key} value={item.key}>
                  {item.value}
                </MenuItem>
              ))}
          </Select>
          {!!errors.Platform && (
            <FormHelperText>{errors.Platform}</FormHelperText>
          )}
        </FormControl>
      </Grid>

      <Grid item xs={12} sm={12} md={4}>
        <Box
          component='p'
          display='flex'
          alignItems='center'
          color='text.primary'
          fontSize={14}
          mb={2}
        >
          Application Access Point
          <Box component='span' ml={1} sx={{ color: 'error.main' }}>
            *
          </Box>
          <AppGlossary title='Select if the app will be accessed and used directly within or outside the Joblogic Marketplace portal.' />
        </Box>
        <FormControl
          disabled={checkIsModuleMode(values.IntegrationMode)}
          sx={{ '& .MuiFormGroup-root': { height: 55 } }}
        >
          <RadioGroup row>
            <FormControlLabel
              checked={values.LaunchType === LAUNCH_TYPE_ENUM['Internal']}
              value={LAUNCH_TYPE_ENUM['Internal']}
              control={<Radio />}
              label='In-Marketplace'
              onChange={() =>
                setFieldValue('LaunchType', LAUNCH_TYPE_ENUM['Internal'])
              }
            />
            <FormControlLabel
              checked={values.LaunchType === LAUNCH_TYPE_ENUM['External']}
              value={LAUNCH_TYPE_ENUM['External']}
              control={<Radio />}
              label='Off-Marketplace'
              onChange={() =>
                setFieldValue('LaunchType', LAUNCH_TYPE_ENUM['External'])
              }
            />
          </RadioGroup>
        </FormControl>
      </Grid>

      <Grid item xs={12} sm={12} md={4}>
        <Box
          component='p'
          display='flex'
          alignItems='center'
          color='text.primary'
          fontSize={14}
          mb={2}
        >
          Access Scope
          <Box component='span' ml={1} sx={{ color: 'error.main' }}>
            *
          </Box>
          <AppGlossary title='Sets the access scope for the app: TenantWide enables tenant-wide access, UserSpecific requires assigning users individually.' />
        </Box>
        <FormControl
          disabled={checkIsModuleMode(values.IntegrationMode)}
          sx={{ '& .MuiFormGroup-root': { height: 55 } }}
        >
          <RadioGroup row>
            <FormControlLabel
              checked={values.AccessScope === ACCESS_SCOPE_ENUM['Tenant']}
              value={ACCESS_SCOPE_ENUM['Tenant']}
              control={<Radio />}
              label='Tenant'
              onChange={() =>
                setFieldValue('AccessScope', ACCESS_SCOPE_ENUM['Tenant'])
              }
            />
            <FormControlLabel
              checked={values.AccessScope === ACCESS_SCOPE_ENUM['User']}
              value={ACCESS_SCOPE_ENUM['User']}
              control={<Radio />}
              label='User'
              onChange={() =>
                setFieldValue('AccessScope', ACCESS_SCOPE_ENUM['User'])
              }
            />
          </RadioGroup>
        </FormControl>
      </Grid>

      <Grid item xs={12} sm={12} md={6}>
        <FormControl component='fieldset' variant='standard'>
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  checked={values.IsLegacy}
                  onChange={(e) => {
                    setFieldValue('IsLegacy', e.target.checked);
                  }}
                />
              }
              label={
                <>
                  IFrame Token Support
                  <AppGlossary
                    title={`
                          Enable this option to pass a security token directly to the embedded app within the iframe.
                          When enabled, this option provides seamless authentication by securely passing a token from Joblogic Marketplace to the embedded app, eliminating the need for users to re-authenticate on the embedded site.
                          This allows the external app to recognize and trust the user based on their initial Joblogic Marketplace login, ensuring a smooth experience and improved security for embedded applications that require token-based authentication
                          `}
                  />
                </>
              }
            />
          </FormGroup>
        </FormControl>
      </Grid>

      {isShowWebIntegrationMode && (
        <Grid item xs={12} sm={12}>
          <Box sx={{ '& .MuiTypography-h4': { mb: 5 } }}>
            <Typography variant='h4'>Web Configurations</Typography>
            <Grid container spacing={5}>
              <Grid item xs={12} sm={6} md={6}>
                <Box
                  component='p'
                  display='flex'
                  alignItems='center'
                  color='text.primary'
                  fontSize={14}
                  mb={2}
                >
                  Web Integration Mode
                  <Box component='span' ml={1} sx={{ color: 'error.main' }}>
                    *
                  </Box>
                  <AppGlossary title='Select how your app will integrate with the Joblogic System (This is for Web platform only)' />
                </Box>
                <FormControl
                  fullWidth
                  error={!!errors.IntegrationMode}
                  sx={{ '& .MuiFormHelperText-root': { display: 'none' } }}
                >
                  <Select
                    key={values.IntegrationMode}
                    value={values.IntegrationMode}
                    onChange={(e) => {
                      setFieldValue('IntegrationMode', e.target.value);

                      if (!checkIsModuleMode(e.target.value)) {
                        setFieldValue('ModuleId', '', false);
                        return;
                      }

                      if (checkIsModuleMode(e.target.value)) {
                        setFieldValue(
                          'LaunchType',
                          LAUNCH_TYPE_ENUM['External'],
                          false,
                        );
                      }
                    }}
                  >
                    {APP_TYPE_ENUM_SELECT.map((item) => (
                      <MenuItem
                        key={item.value}
                        value={item.value}
                        sx={{
                          width: '100%',
                          overflow: 'hidden',
                          whiteSpace: 'pre',
                          p: 2,
                        }}
                      >
                        <Tooltip title={item.description}>
                          <Box>
                            {item.label}
                            <FormHelperText
                              display='block'
                              sx={{
                                fontSize: 10,
                                textOverflow: 'ellipsis',
                                overflow: 'hidden',
                              }}
                            >
                              {item.description}
                            </FormHelperText>
                          </Box>
                        </Tooltip>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {!!errors.IntegrationMode && (
                  <FormHelperText sx={{ color: 'error.main' }}>
                    {errors.IntegrationMode}
                  </FormHelperText>
                )}
              </Grid>

              <Grid item xs={12} sm={6} md={6}>
                <Box
                  component='p'
                  color='text.primary'
                  display='flex'
                  alignItems='center'
                  fontSize={14}
                  mb={2}
                >
                  Module Id
                  {checkIsModuleMode(values.IntegrationMode) && (
                    <Box component='span' ml={1} sx={{ color: 'error.main' }}>
                      *
                    </Box>
                  )}
                  <AppGlossary title='The feature ID within Joblogic System.' />
                </Box>

                <AppModuleSelect
                  error={!!errors.ModuleModel}
                  helperText={errors.ModuleModel}
                  value={values.ModuleModel ?? null}
                  onChange={(e, value) => {
                    setFieldValue('ModuleId', value?.Id);
                    setFieldValue('ModuleName', value?.Name);
                    setFieldValue('ModuleModel', value);
                  }}
                />
              </Grid>

              {!checkIsModuleMode(values.IntegrationMode) && (
                <Grid item xs={12} sm={6} md={6}>
                  <Box
                    component='p'
                    display='flex'
                    alignItems='center'
                    color='text.primary'
                    fontSize={14}
                    mb={2}
                  >
                    Hosted App Address
                    <AppGlossary
                      title='Add the URL of your hosted app to make the shell app operational.
          This field links the shell app to the actual backend or frontend infrastructure of the hosted app.
          By entering a valid URL, the shell app transitions from a placeholder to a fully operational state'
                    />
                  </Box>
                  <TextField
                    placeholder='Enter text'
                    name='AppUrl'
                    error={!!errors.AppUrl}
                    helperText={errors.AppUrl}
                    value={values.AppUrl ?? ''}
                    onChange={async (e) => {
                      setFieldValue('AppUrl', e.target.value);
                    }}
                    onBlur={(e) => {
                      setFieldValue('AppUrl', values.AppUrl?.trim());
                    }}
                    fullWidth
                  />
                </Grid>
              )}

              <Grid item xs={12} sm={12} md={12}>
                <Box component='p' color='text.primary' fontSize={14} mb={2}>
                  Web App Code
                  <Box component='span' ml={1} sx={{ color: 'error.main' }}>
                    *
                  </Box>
                  <AppGlossary title='A unique identifier for the app, used to distinguish it from others. This code should not be changed unless absolutely necessary.' />
                </Box>

                <FormControl fullWidth error={Boolean(errors.AppCode)}>
                  <TextField
                    fullWidth
                    placeholder='Enter uuid'
                    name='AppCode'
                    error={!!errors.AppCode}
                    helperText={errors.AppCode}
                    value={values.AppCode ?? ''}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position='end'>
                          <IconButton
                            title='Generate UUID'
                            onClick={() =>
                              setFieldValue('AppCode', generateUUID())
                            }
                            edge='end'
                          >
                            <ReplayIcon />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    onChange={async (e) => {
                      setFieldValue('AppCode', e.target.value);
                    }}
                    onBlur={(e) => {
                      setFieldValue('AppCode', values.AppCode?.trim());
                    }}
                  />
                </FormControl>
              </Grid>

              {isHaveFeatureStep && (
                <Grid item xs={12} sm={12}>
                  <Box sx={{ '& .MuiTypography-h4': { mb: 5 } }}>
                    <Tabs
                      value={activeTab}
                      onChange={handleChangeTab}
                      sx={{ mb: 4 }}
                    >
                      <Tab label='BAU' value={'PageSlots'} />
                      <Tab label='Customer Portal' value={'PageSlotsPortal'} />
                    </Tabs>

                    <FeatureStepWebMarketPlace
                      values={values}
                      errors={errors}
                      keyField={activeTab}
                      setFieldValue={setFieldValue}
                    />
                  </Box>
                </Grid>
              )}
            </Grid>
          </Box>
        </Grid>
      )}

      {isHaveMobileStep && (
        <>
          <Grid item xs={12} sm={12}>
            <Divider />
          </Grid>

          <Grid item xs={12} sm={12}>
            <Box sx={{ '& .MuiTypography-h4': { mb: 5 } }}>
              <Typography variant='h4'>Mobile</Typography>
              <MobileStep
                values={values}
                errors={errors}
                setFieldValue={setFieldValue}
                resetMobileFieldsIfNeeded={resetMobileFieldsIfNeeded}
              />
            </Box>
          </Grid>
        </>
      )}
    </AppGridContainer>
  );
};

export default React.memo(ConfigurationSettings);

ConfigurationSettings.propTypes = {
  errors: PropTypes.object,
  values: PropTypes.object,
  setFieldValue: PropTypes.func,
};
